import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlaggedList } from '@/components/capture/FlaggedList';
import { listCaptures } from '@/lib/api';
import type { CaptureRecord } from '@/lib/api/types';

/**
 * CA-08: My flagged captures (wireframes/capture-app.html #ca08). Two
 * kinds of capture need the operator, and this screen lists both.
 *
 * A manager asked for a re-capture. Those come from the API
 * (`GET /capture?status=RECAPTURE_REQUESTED`), each opening on the shared
 * review route where the operator retakes the frame. This half used to be
 * the wireframe's structure with no data behind it, so a manager's request
 * reached no one (run 1 finding 11-003).
 *
 * An upload failed on the device. Those come from the offline queue
 * (`FlaggedList`), with retry and discard.
 */
export function FlaggedCaptures() {
  const [recaptures, setRecaptures] = useState<CaptureRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listCaptures({ status: 'RECAPTURE_REQUESTED' })
      .then((page) => {
        if (!cancelled) setRecaptures(page.items);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Could not load the re-capture requests');
          setRecaptures([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold">Flagged for you</h1>
        <Badge tone="warning">Needs attention</Badge>
      </div>

      <section aria-label="Re-capture requests" className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">Your manager asked for these again</h2>
        {recaptures === null && <p className="text-sm text-muted">Loading re-capture requests…</p>}
        {error && <p className="text-sm text-warning">{error}</p>}
        {recaptures !== null && recaptures.length === 0 && !error && (
          <p className="text-sm text-muted">No re-capture requests. Nothing your manager sent back is waiting.</p>
        )}
        {recaptures !== null && recaptures.length > 0 && (
          <ul className="flex flex-col gap-2">
            {recaptures.map((capture) => (
              <li key={capture.captureId}>
                <RecaptureRow capture={capture} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Upload failures" className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">Uploads that failed on this device</h2>
        <FlaggedList />
      </section>
    </div>
  );
}

/** The reason the manager gave, from the latest re-capture verdict on the trail. */
function recaptureReason(capture: CaptureRecord): string | null {
  const event = [...(capture.reviewTrail ?? [])].reverse().find((item) => item.verdict === 'request_recapture');
  return event?.note ?? null;
}

function RecaptureRow({ capture }: { capture: CaptureRecord }) {
  const description = capture.fields?.description?.value;
  const reason = recaptureReason(capture);
  return (
    <Link
      to={`/capture/${encodeURIComponent(capture.captureId)}/result`}
      className="flex min-h-11 items-center gap-3 rounded-card border border-border p-3 text-sm hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="break-words">{description ? String(description) : 'Unnamed item'}</span>
        {reason && <span className="text-xs text-muted">&ldquo;{reason}&rdquo;</span>}
        <span className="text-xs text-muted">
          Captured {new Date(capture.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
        </span>
      </span>
      <Badge tone="warning">Retake</Badge>
      <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden="true" />
    </Link>
  );
}
